import sharedSupabase from '../config/supabase';

/**
 * Which vendor product each count line will be bought from.
 *
 * `count_items.selected_purchase_option_id` (migration 20260729130000) is set
 * from two places — the template editor, where it is the line's default
 * vendor, and the count tool, where a counter picks an alternate at counting
 * time. NULL everywhere means "automatic: the lowest trustworthy comparable
 * price under the item's relationship comparison, preferred vendor as
 * fallback" — resolved at read time by resolveSelectedOption. Clearing a
 * choice writes NULL back; the automatic winner is never backfilled into the
 * column, so the line keeps following the market.
 *
 * The column arrives with a migration that may not be deployed everywhere, so
 * every call degrades to a warning + null rather than breaking a count.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

// PostgREST/Postgres "column not deployed yet" signatures.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['PGRST204', 'PGRST205', '42703'].includes(code)
    || /column .* does not exist|schema cache/i.test(message);
};

const warnMissing = (error) => {
  console.warn(
    '[count-item-selection] selected_purchase_option_id unavailable (migration not deployed?):',
    error?.message,
  );
};

/**
 * Pins a count line to one vendor product, or clears the pin.
 *
 * @param {number|string} countItemId
 * @param {string|null} purchaseOptionId - null returns the line to the item's
 *                                         preferred option.
 * @returns {Promise<boolean>} false when the column is not deployed.
 */
export async function setSelectedPurchaseOption(countItemId, purchaseOptionId, client = sharedSupabase) {
  if (!countItemId) throw new Error('countItemId is required');
  const { error } = await client
    .from('count_items')
    .update({ selected_purchase_option_id: purchaseOptionId || null })
    .eq('id', countItemId);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return false;
    }
    throw new Error(error.message);
  }
  return true;
}

/**
 * Applies one vendor choice to SEVERAL count lines at once — the same item
 * counted in several locations shares a vendor, so changing it on one line and
 * not its siblings would produce two different answers for one purchase.
 *
 * @param {Array<number|string>} countItemIds
 * @param {string|null} purchaseOptionId
 * @returns {Promise<boolean>} false when the column is not deployed.
 */
export async function setSelectedPurchaseOptionForLines(countItemIds, purchaseOptionId, client = sharedSupabase) {
  const ids = [...new Set((countItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return true;
  const { error } = await client
    .from('count_items')
    .update({ selected_purchase_option_id: purchaseOptionId || null })
    .in('id', ids);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return false;
    }
    throw new Error(error.message);
  }
  return true;
}

/**
 * Current vendor choice for a set of count lines, as
 * { [countItemId]: purchaseOptionId|null }. Returns {} when the column is not
 * deployed, which the resolver reads as "no explicit choice" — the same as an
 * unpinned line.
 */
export async function fetchSelectedOptionsForCountItems(countItemIds, client = sharedSupabase) {
  const ids = [...new Set((countItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return {};
  const { data, error } = await client
    .from('count_items')
    .select('id, selected_purchase_option_id')
    .in('id', ids);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return {};
    }
    throw new Error(error.message);
  }
  const byId = {};
  (data || []).forEach((row) => { byId[row.id] = row.selected_purchase_option_id ?? null; });
  return byId;
}

/* ── Count format ─────────────────────────────────────────────────────────── */
//
// A different question from the one above, deliberately stored in different
// columns. `selected_purchase_option_id` is who we will BUY from;
// `count_display_purchase_option_id` (migration 20260731120000) is the package
// we COUNT in. A store may count Costco tubs all week and order from Sysco,
// and nothing in this section touches the ordering choice.

const COUNT_FORMAT_COLUMNS = [
  'count_display_purchase_option_id',
  'count_display_unit',
  'follow_latest_purchase',
  'count_display_conversion_snapshot',
];

/**
 * Applies a count format to specific count lines.
 *
 * Unlike the vendor choice, this is NOT automatically pushed to sibling
 * locations: the walk-in may hold Costco tubs while the line holds Sysco jars,
 * and that is one item with two right answers. The caller passes exactly the
 * lines it means.
 *
 * @param {Array<number|string>} countItemIds
 * @param {object|null} patch  from `countFormatSelection`, or null to unpin
 * @returns {Promise<boolean>} false when the columns are not deployed.
 */
export async function setCountFormatForLines(countItemIds, patch, client = sharedSupabase) {
  const ids = [...new Set((countItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return true;

  const update = patch
    ? Object.fromEntries(COUNT_FORMAT_COLUMNS.filter((c) => c in patch).map((c) => [c, patch[c]]))
    : {
        count_display_purchase_option_id: null,
        count_display_unit: null,
        count_display_conversion_snapshot: null,
      };
  if (Object.keys(update).length === 0) return true;

  const { error } = await client.from('count_items').update(update).in('id', ids);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return false;
    }
    throw new Error(error.message);
  }
  return true;
}

/**
 * Applies a count format to TEMPLATE rows (count_template_items) — the
 * template editor's counterpart of setCountFormatForLines. A pin made here is
 * the item/template/location default every future count inherits (session
 * creation copies nothing, so counts read it through the template-memory
 * merge at session load).
 *
 * @param {Array<number|string>} templateItemIds
 * @param {object|null} patch  from `countFormatSelection`, or null to unpin
 * @returns {Promise<boolean>} false when the columns are not deployed.
 */
export async function setCountFormatForTemplateItems(templateItemIds, patch, client = sharedSupabase) {
  const ids = [...new Set((templateItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return true;

  const update = patch
    ? Object.fromEntries(COUNT_FORMAT_COLUMNS.filter((c) => c in patch).map((c) => [c, patch[c]]))
    : {
        count_display_purchase_option_id: null,
        count_display_unit: null,
        count_display_conversion_snapshot: null,
      };
  if (Object.keys(update).length === 0) return true;

  const { error } = await client.from('count_template_items').update(update).in('id', ids);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return false;
    }
    throw new Error(error.message);
  }
  return true;
}

/**
 * The stored count format for a set of lines. Returns {} when the columns are
 * not deployed, which resolves to the same answer a newly configured store
 * gets: follow the latest verified purchase.
 */
export async function fetchCountFormatsForCountItems(countItemIds, client = sharedSupabase) {
  const ids = [...new Set((countItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return {};
  const { data, error } = await client
    .from('count_items')
    .select(`id, ${COUNT_FORMAT_COLUMNS.join(', ')}`)
    .in('id', ids);
  if (error) {
    if (isMissingColumnError(error)) {
      warnMissing(error);
      return {};
    }
    throw new Error(error.message);
  }
  const byId = {};
  (data || []).forEach((row) => {
    byId[row.id] = {
      count_display_purchase_option_id: row.count_display_purchase_option_id ?? null,
      count_display_unit: row.count_display_unit ?? null,
      follow_latest_purchase: row.follow_latest_purchase !== false,
      count_display_conversion_snapshot: row.count_display_conversion_snapshot ?? null,
    };
  });
  return byId;
}
